import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ExtraInfoEntry, ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit, faHandHoldingDollar, faTrash } from "@fortawesome/free-solid-svg-icons";
import { showAlert } from "@utils/Alert";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { ZoneApi } from "@/api/ZoneApi";
import { SalemanApi } from "@/api/SalemenApi";
import { useAppSelector } from "@/store/hooks";

const ClientIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const showZoneSaleman = useAppSelector(state => state.suscription.suscription?.show_zone_saleman_on_clients) || false;
  const [zones, setZones] = useState<Record<string, string>>({});
  const [salemen, setSalemen] = useState<Record<string, string>>({});

  /**
   * El listado se sirve desde Meilisearch, cuyos documentos solo traen `zoneId` y
   * `salemanId`. Los nombres se resuelven aqui con `optionsList()`, que va cacheado
   * (Zone y Saleman estan en CACHEABLE_MODELS), y solo cuando el perfil lo pide.
   */
  useEffect(() => {
    if (!showZoneSaleman) {
      return;
    }

    const loadLabels = async () => {
      const [zoneOptions, salemanOptions] = await Promise.all([
        ZoneApi.optionsList(),
        SalemanApi.optionsList()
      ]);
      setZones(zoneOptions);
      setSalemen(salemanOptions);
    }
    loadLabels()
  }, [showZoneSaleman]);

  const buildExtraInfo = (client: ClientDataType): ExtraInfoEntry[] => {
    return [
      { title: "Zona", value: client.zoneId ? zones[client.zoneId] : "" },
      { title: "Vendedor", value: client.salemanId ? salemen[client.salemanId] : "" }
    ]
  }

  const deleteRecord = async (id: string) => {
    const { errors } = await ClientApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'El Cliente no puedo ser eliminado');
    } else {
      showAlert('success', 'El Cliente fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  return (
    <PageContainer
      title="Lista de Clientes"
      backUrl="/user/dashboard"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/client')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
      secondaryActions={[
        {
          actionTitle: "Reportes",
          href: "/user/dashboard-clients"
        },
        {
          actionTitle: "Importar Clientes",
          href: "/user/clients/import"
        },
        {
          actionTitle: "Proveedores de Envio",
          href: "/user/shipping-providers"
        }
      ]}
    >
      <ModelIndex<ClientDataType>
        ref={modelRef}
        meiliConfig={{
          index: "clients",
          sort: ["nombre:asc"]
        }}
        fetchData={(filters, token, limit) => ClientApi.fetchAll(filters, token, limit, null, { noCache: true })}
        filterProps={[
          {
            key: "codigo",
            filterType: "eq",
            type: "number"
          },
          {
            key: "nombre",
            filterType: "contains"
          },
          {
            key: "nombre_comercial",
            placeholder: "Nombre Comercial",
            filterType: "contains"
          },
          {
            key: "email",
            filterType: "contains"
          }
        ]}
        primaryProperties={[
          {
            key: "codigo",
            highlighted: true
          },
          {
            key: "nombre",
            alignment: "Start"
          },
        ]}
        extraInfoRow={showZoneSaleman ? buildExtraInfo : undefined}
        secondaryProperties={[
          {
            key: "nombre_comercial",
            title: "Nombre Comercial",
          },
          {
            key: "telefono1",
            title: "Tel 1",
          },
          {
            key: "email",
            title: "Email"
          }
        ]}
        secondaryPropertiesSizing={[
          { sm: "4", md: "5" },
          { sm: "3", md: "2" },
          { sm: "3", md: "3" },
        ]}
        listActions={[
          {
            href: "/user/client/{:id}",
            actionTitle: "Editar Cliente",
            iconProps: { icon: faEdit }
          },
          {
            href: "/user/clients/credits/{:id}",
            actionTitle: "Saldos a Favor",
            iconProps: { icon: faHandHoldingDollar }
          },
          {
            actionTitle: "Eliminar Cliente",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Cliente",
              confirmMessage: "¿Seguro que desea eliminar este Cliente?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>

    </PageContainer >
  );
}

export default ClientIndex;
